import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { useEffect, useState } from 'react';
import type { Ref } from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';
import { ApiError } from '../lib/api';
import { centsToDollars, dollarsToCents } from '../lib/format';

/**
 * React Hook Form field wrappers. Each shows the field's validation message and marks
 * required fields with an asterisk, as the reference does.
 */

interface BaseProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
}

export interface Option {
  value: string;
  label: string;
  /** Shown but not choosable. */
  disabled?: boolean;
}

export function TextInput<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helperText,
  ...rest
}: BaseProps<T> & Omit<TextFieldProps, 'name' | 'label' | 'required' | 'helperText'>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          {...rest}
          {...field}
          value={field.value ?? ''}
          label={label}
          required={required}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? helperText}
        />
      )}
    />
  );
}

/** Whole-number input stored as a number; empty stores `emptyValue` (default undefined). */
export function NumberInput<T extends FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  helperText,
  emptyValue,
}: BaseProps<T> & { emptyValue?: null | undefined | number }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          label={label}
          required={required}
          disabled={disabled}
          inputMode="numeric"
          name={field.name}
          inputRef={field.ref}
          onBlur={field.onBlur}
          value={field.value === null || field.value === undefined ? '' : String(field.value)}
          onChange={(e) => {
            const text = e.target.value.replace(/[^\d]/g, '');
            field.onChange(text === '' ? emptyValue : Number(text));
          }}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? helperText}
        />
      )}
    />
  );
}

/** Dollar amount typed by the user, stored in the form as integer cents. */
export function MoneyInput<T extends FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  helperText,
}: BaseProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <MoneyField
          label={label}
          required={required}
          disabled={disabled}
          cents={field.value as number | undefined}
          onCents={field.onChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          name={field.name}
          error={fieldState.error?.message}
          helperText={helperText}
        />
      )}
    />
  );
}

function MoneyField(props: {
  label: string;
  required?: boolean;
  disabled?: boolean;
  cents: number | undefined;
  onCents: (cents: number | undefined) => void;
  onBlur: () => void;
  inputRef: Ref<HTMLInputElement>;
  name: string;
  error?: string;
  helperText?: string;
}) {
  // Keep the raw text so the user can type "12." on the way to "12.50".
  const [text, setText] = useState(centsToDollars(props.cents));
  useEffect(() => {
    // NaN is what this field itself reports while the text is not yet a valid amount
    // ("12." on the way to "12.50"); leave the user's text alone in that case.
    if (typeof props.cents === 'number' && Number.isNaN(props.cents)) return;
    if (dollarsToCents(text) !== props.cents) setText(centsToDollars(props.cents));
    // Only resync when the form value changes from outside (reset, load).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.cents]);

  return (
    <TextField
      label={props.label}
      required={props.required}
      disabled={props.disabled}
      name={props.name}
      inputRef={props.inputRef}
      inputMode="decimal"
      placeholder="0.00"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        // NaN makes the schema report "expected number" instead of silently storing 0.
        props.onCents(
          e.target.value.trim() === '' ? undefined : (dollarsToCents(e.target.value) ?? NaN),
        );
      }}
      onBlur={props.onBlur}
      error={Boolean(props.error)}
      helperText={props.error ?? props.helperText}
      slotProps={{ input: { startAdornment: <span style={{ marginRight: 4 }}>$</span> } }}
    />
  );
}

export function SelectInput<T extends FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  helperText,
  options,
  emptyLabel,
}: BaseProps<T> & { options: readonly Option[]; emptyLabel?: string }) {
  const id = `select-${name}`;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormControl error={Boolean(fieldState.error)} required={required} disabled={disabled}>
          <InputLabel id={`${id}-label`}>{label}</InputLabel>
          <Select
            labelId={`${id}-label`}
            id={id}
            label={label}
            name={field.name}
            inputRef={field.ref}
            onBlur={field.onBlur}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          >
            {emptyLabel !== undefined ? (
              <MenuItem value="">
                <em>{emptyLabel}</em>
              </MenuItem>
            ) : null}
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{fieldState.error?.message ?? helperText}</FormHelperText>
        </FormControl>
      )}
    />
  );
}

/** Yes / No select bound to a boolean (or null when `nullable` and nothing is chosen). */
export function YesNoInput<T extends FieldValues>({
  nullable,
  ...props
}: BaseProps<T> & { nullable?: boolean }) {
  const id = `yesno-${props.name}`;
  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field, fieldState }) => (
        <FormControl
          error={Boolean(fieldState.error)}
          required={props.required}
          disabled={props.disabled}
        >
          <InputLabel id={`${id}-label`}>{props.label}</InputLabel>
          <Select
            labelId={`${id}-label`}
            id={id}
            label={props.label}
            name={field.name}
            inputRef={field.ref}
            onBlur={field.onBlur}
            value={field.value === true ? 'yes' : field.value === false ? 'no' : ''}
            onChange={(e) =>
              field.onChange(
                e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null,
              )
            }
          >
            {nullable ? (
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
            ) : null}
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
          <FormHelperText>{fieldState.error?.message ?? props.helperText}</FormHelperText>
        </FormControl>
      )}
    />
  );
}

export function MultiSelectInput<T extends FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  helperText,
  options,
}: BaseProps<T> & { options: readonly Option[] }) {
  const id = `multi-${name}`;
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value: string[] = Array.isArray(field.value) ? field.value : [];
        return (
          <FormControl error={Boolean(fieldState.error)} required={required} disabled={disabled}>
            <InputLabel id={`${id}-label`}>{label}</InputLabel>
            <Select
              multiple
              labelId={`${id}-label`}
              id={id}
              label={label}
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              value={value}
              onChange={(e) => field.onChange(e.target.value)}
              renderValue={(selected) =>
                selected.length > 4
                  ? `${selected.length} selected`
                  : selected.map(labelOf).join(', ')
              }
              MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
            >
              {options.map((o) => (
                <MenuItem key={o.value} value={o.value} dense>
                  <Checkbox size="small" checked={value.includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{fieldState.error?.message ?? helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}

export function CheckboxInput<T extends FieldValues>({
  control,
  name,
  label,
  disabled,
}: BaseProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormControlLabel
          label={label}
          control={
            <Checkbox
              size="small"
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={disabled}
            />
          }
        />
      )}
    />
  );
}

/**
 * Copies field errors from an API validation response onto the form.
 * Returns true when at least one field was marked, so the caller can skip the generic toast.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
): boolean {
  if (!(error instanceof ApiError) || error.details.length === 0) return false;
  for (const d of error.details)
    setError(d.path as FieldPath<T>, { type: 'server', message: d.message });
  return true;
}

export const toOptions = (values: readonly string[]): Option[] =>
  values.map((v) => ({ value: v, label: v }));
